import React from "react";
import { DollarSign, Percent, TrendingDown, Info, Calculator, Tag } from 'lucide-react';
import { NumberInput } from "../../common/NumberInput";

export default function PricingTab({ values, setField, t }) {
  const calculateDiscountedPrice = (
    originalPrice,
    discountPercentage,
    meals
  ) => {
    const total = originalPrice * meals;
    const discount = (total * discountPercentage) / 100;
    return (total - discount).toFixed(2);
  };

  const basePrice = parseFloat(values.pricePerDay) || 0;

  return (
    <div className="space-y-8">
      {/* Bulk Discounts Configuration */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 px-6 py-4 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg mr-3">
              <Percent className="w-5 h-5 text-purple-600" />
            </div>
            {t("bulkDiscounts")}
          </h3>
          <p className="text-sm text-gray-600 mt-1">{t('configureBulkDiscountRates')}</p>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="space-y-2">
              <div className="flex items-center mb-2">
                <Tag className="w-4 h-4 text-gray-500 mr-2" />
                <span className="text-sm font-medium text-gray-700">{t("offer1Week")}</span>
              </div>
              <NumberInput
                name="offerPercentage1Week"
                value={values.offerPercentage1Week}
                onChange={(e) => setField("offerPercentage1Week", e.target.value)}
                min={0}
                max={100}
                step={1}
                help={t("discountPercentage")}
                className="focus:ring-purple-500 focus:border-purple-500"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center mb-2">
                <Tag className="w-4 h-4 text-gray-500 mr-2" />
                <span className="text-sm font-medium text-gray-700">{t("offer2Weeks")}</span>
              </div>
              <NumberInput
                name="offerPercentage2Weeks"
                value={values.offerPercentage2Weeks}
                onChange={(e) =>
                  setField("offerPercentage2Weeks", e.target.value)
                }
                min={0}
                max={100}
                step={1}
                help={t("discountPercentage")}
                className="focus:ring-purple-500 focus:border-purple-500"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center mb-2">
                <Tag className="w-4 h-4 text-gray-500 mr-2" />
                <span className="text-sm font-medium text-gray-700">{t("offer4Weeks")}</span>
              </div>
              <NumberInput
                name="offerPercentage4Weeks"
                value={values.offerPercentage4Weeks}
                onChange={(e) =>
                  setField("offerPercentage4Weeks", e.target.value)
                }
                min={0}
                max={100}
                step={1}
                help={t("discountPercentage")}
                className="focus:ring-purple-500 focus:border-purple-500"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center mb-2">
                <Tag className="w-4 h-4 text-gray-500 mr-2" />
                <span className="text-sm font-medium text-gray-700">{t("offer8Weeks")}</span>
              </div>
              <NumberInput
                name="offerPercentage8Weeks"
                value={values.offerPercentage8Weeks}
                onChange={(e) =>
                  setField("offerPercentage8Weeks", e.target.value)
                }
                min={0}
                max={100}
                step={1}
                help={t("discountPercentage")}
                className="focus:ring-purple-500 focus:border-purple-500"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Pricing Preview */}
      {basePrice > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="bg-gradient-to-r from-indigo-50 to-blue-50 px-6 py-4 border-b border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <div className="p-2 bg-indigo-100 rounded-lg mr-3">
                <Calculator className="w-5 h-5 text-indigo-600" />
              </div>
              {t("pricingPreview")}
            </h3>
            <p className="text-sm text-gray-600 mt-1">{t('realTimePricingCalculation')}</p>
          </div>
          
          <div className="p-6">
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start">
                <Info className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-blue-800">
                  {t("pricingPreviewNote")}
                </p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* 1 Week */}
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6 border border-gray-200 hover:shadow-md transition-shadow">
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-10 h-10 bg-gray-100 rounded-full mb-3">
                    <DollarSign className="w-5 h-5 text-gray-600" />
                  </div>
                  <div className="text-lg font-semibold text-gray-900 mb-3">
                    {t("oneWeek")}
                  </div>
                  {values.offerPercentage1Week > 0 ? (
                    <div>
                      <div className="text-sm text-gray-400 line-through mb-1">
                        {(basePrice * 5).toFixed(2)} JD
                      </div>
                      <div className="text-2xl font-bold text-purple-600 mb-2">
                        {calculateDiscountedPrice(
                          basePrice,
                          values.offerPercentage1Week,
                          5
                        )}{" "}
                        JD
                      </div>
                      <div className="inline-flex items-center px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                        <TrendingDown className="w-3 h-3 mr-1" />
                        {values.offerPercentage1Week}% {t("off")}
                      </div>
                    </div>
                  ) : (
                    <div className="text-2xl font-bold text-gray-900">
                      {(basePrice * 5).toFixed(2)} JD
                    </div>
                  )}
                </div>
              </div>

              {/* 2 Weeks */}
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 border border-blue-200 hover:shadow-md transition-shadow">
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-10 h-10 bg-blue-100 rounded-full mb-3">
                    <DollarSign className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="text-lg font-semibold text-gray-900 mb-3">
                    {t("twoWeeks")}
                  </div>
                  {values.offerPercentage2Weeks > 0 ? (
                    <div>
                      <div className="text-sm text-gray-400 line-through mb-1">
                        {(basePrice * 10).toFixed(2)} JD
                      </div>
                      <div className="text-2xl font-bold text-purple-600 mb-2">
                        {calculateDiscountedPrice(
                          basePrice,
                          values.offerPercentage2Weeks,
                          10
                        )}{" "}
                        JD
                      </div>
                      <div className="inline-flex items-center px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                        <TrendingDown className="w-3 h-3 mr-1" />
                        {values.offerPercentage2Weeks}% {t("off")}
                      </div>
                    </div>
                  ) : (
                    <div className="text-2xl font-bold text-gray-900">
                      {(basePrice * 10).toFixed(2)} JD
                    </div>
                  )}
                </div>
              </div>

              {/* 4 Weeks */}
              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 border border-green-200 hover:shadow-md transition-shadow">
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-10 h-10 bg-green-100 rounded-full mb-3">
                    <DollarSign className="w-5 h-5 text-green-600" />
                  </div>
                  <div className="text-lg font-semibold text-gray-900 mb-3">
                    {t("fourWeeks")}
                  </div>
                  {values.offerPercentage4Weeks > 0 ? (
                    <div>
                      <div className="text-sm text-gray-400 line-through mb-1">
                        {(basePrice * 20).toFixed(2)} JD
                      </div>
                      <div className="text-2xl font-bold text-purple-600 mb-2">
                        {calculateDiscountedPrice(
                          basePrice,
                          values.offerPercentage4Weeks,
                          20
                        )}{" "}
                        JD
                      </div>
                      <div className="inline-flex items-center px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                        <TrendingDown className="w-3 h-3 mr-1" />
                        {values.offerPercentage4Weeks}% {t("off")}
                      </div>
                    </div>
                  ) : (
                    <div className="text-2xl font-bold text-gray-900">
                      {(basePrice * 20).toFixed(2)} JD
                    </div>
                  )}
                </div>
              </div>

              {/* 8 Weeks */}
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6 border border-purple-200 hover:shadow-md transition-shadow">
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-10 h-10 bg-purple-100 rounded-full mb-3">
                    <DollarSign className="w-5 h-5 text-purple-600" />
                  </div>
                  <div className="text-lg font-semibold text-gray-900 mb-3">
                    {t("eightWeeks")}
                  </div>
                  {values.offerPercentage8Weeks > 0 ? (
                    <div>
                      <div className="text-sm text-gray-400 line-through mb-1">
                        {(basePrice * 40).toFixed(2)} JD
                      </div>
                      <div className="text-2xl font-bold text-purple-600 mb-2">
                        {calculateDiscountedPrice(
                          basePrice,
                          values.offerPercentage8Weeks,
                          40
                        )}{" "}
                        JD
                      </div>
                      <div className="inline-flex items-center px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                        <TrendingDown className="w-3 h-3 mr-1" />
                        {values.offerPercentage8Weeks}% {t("off")}
                      </div>
                    </div>
                  ) : (
                    <div className="text-2xl font-bold text-gray-900">
                      {(basePrice * 40).toFixed(2)} JD
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Savings Summary */}
            <div className="mt-8 p-6 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200">
              <h4 className="text-lg font-semibold text-green-900 mb-4 flex items-center">
                <TrendingDown className="w-5 h-5 text-green-600 mr-2" />
                {t('potentialSavings')}
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { period: t("oneWeek"), discount: values.offerPercentage1Week, meals: 5 },
                  { period: t("twoWeeks"), discount: values.offerPercentage2Weeks, meals: 10 },
                  { period: t("fourWeeks"), discount: values.offerPercentage4Weeks, meals: 20 },
                  { period: t("eightWeeks"), discount: values.offerPercentage8Weeks, meals: 40 }
                ].map((item, index) => (
                  <div key={index} className="text-center">
                    <div className="text-sm text-gray-600 mb-1">{item.period}</div>
                    <div className="text-xl font-bold text-green-700">
                      {item.discount > 0 ? `${((basePrice * item.meals * item.discount) / 100).toFixed(2)} JD` : '0 JD'}
                    </div>
                    <div className="text-xs text-gray-500">{t('saved')}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
